import { attrs, cx, html, type Html, type Part } from "../html.ts";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "glow" | "outline-glow" | "ink";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps {
  label: Part;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** A status or general glyph class suffix, e.g. "copy" renders bt-glyph--copy before the label. */
  icon?: string;
  /** Icon-only buttons need an ariaLabel. The label is not drawn. */
  iconOnly?: boolean;
  ariaLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Renders an anchor styled as a button. */
  href?: string;
  type?: "button" | "submit";
  id?: string;
  /** Data and ARIA attributes, e.g. { "data-action": "approve", "aria-keyshortcuts": "A" }. */
  attrs?: Readonly<Record<string, string | number | boolean | null | undefined>>;
}

/**
 * bt-btn. Glow is for Get started, Approve plan and Promote only: one per view. Danger is outlined, never filled.
 * At most 640px the stylesheet lifts every button to the 44px control size.
 */
export function button(props: ButtonProps): Html {
  const variant = props.variant ?? "secondary";
  if (props.iconOnly && !props.ariaLabel) throw new Error("An icon-only button needs an ariaLabel.");
  const className = cx(
    "bt-btn",
    `bt-btn--${variant}`,
    props.size && props.size !== "md" && `bt-btn--${props.size}`,
    props.iconOnly && "bt-btn--icon",
    props.loading && "is-loading",
  );
  const icon = props.icon ? html`<span class="bt-glyph bt-glyph--${props.icon}" aria-hidden="true"></span>` : "";
  const label = props.iconOnly ? "" : props.label;
  const common = attrs({
    id: props.id,
    class: className,
    "aria-label": props.ariaLabel,
    "aria-busy": props.loading ? "true" : undefined,
    ...props.attrs,
  });
  if (props.href !== undefined && !props.disabled) return html`<a${common} href="${props.href}">${icon}${label}</a>`;
  return html`<button${common} type="${props.type ?? "button"}"${attrs({ disabled: props.disabled || props.loading })}>${icon}${label}</button>`;
}
